/**
 * Device Orientation Utilities
 *
 * Detects smartphone filming angle (overhead crib view vs floor play view)
 * using the DeviceOrientation API.
 */

import { debugLog } from './debugUtilities';
export type FilmingAngle = 'overhead' | 'floor' | 'transitional' | 'unknown';

export interface DeviceOrientationData {
  alpha: number | null; // Rotation around Z axis (compass direction, 0-360)
  beta: number | null;  // Rotation around X axis (front-to-back tilt, -180 to 180)
  gamma: number | null; // Rotation around Y axis (left-to-right tilt, -90 to 90)
  filmingAngle: FilmingAngle;
  isLandscape: boolean;
}

export interface OrientationThresholds {
  overheadMaxBeta: number;  // Max beta for overhead filming (default 40°)
  floorMinBeta: number;     // Min beta for floor filming (default 60°)
}

const DEFAULT_THRESHOLDS: OrientationThresholds = {
  overheadMaxBeta: 40,
  floorMinBeta: 60
};

/**
 * Determines filming angle from device orientation beta value
 */
export function getFilmingAngle(
  beta: number | null,
  thresholds: OrientationThresholds = DEFAULT_THRESHOLDS
): FilmingAngle {
  if (beta === null) {
    return 'unknown';
  }

  // Normalize beta to 0-180 range (handle negative values)
  const normalizedBeta = Math.abs(beta);

  if (normalizedBeta <= thresholds.overheadMaxBeta) {
    return 'overhead'; // Device tilted slightly from horizontal (crib view)
  } else if (normalizedBeta >= thresholds.floorMinBeta) {
    return 'floor'; // Device more vertical (floor play view)
  } else {
    return 'transitional'; // In between angles
  }
}

/**
 * Checks if device is in landscape orientation
 */
export function isLandscapeOrientation(gamma: number | null): boolean {
  if (gamma === null) return false;
  // Landscape when gamma is close to ±90 degrees
  return Math.abs(Math.abs(gamma) - 90) < 30;
}

/**
 * DeviceOrientation event listener manager
 */
export class DeviceOrientationManager {
  private listener: ((data: DeviceOrientationData) => void) | null = null;
  private handleOrientationChange: ((event: DeviceOrientationEvent) => void) | null = null;
  private thresholds: OrientationThresholds;
  private permissionGranted = false;

  constructor(thresholds: OrientationThresholds = DEFAULT_THRESHOLDS) {
    this.thresholds = thresholds;
  }

  /**
   * Request permission for iOS 13+ devices
   */
  async requestPermission(): Promise<boolean> {
    // Check if permission request is needed (iOS 13+)
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof (DeviceOrientationEvent as any).requestPermission === 'function'
    ) {
      try {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        this.permissionGranted = permission === 'granted';
        return this.permissionGranted;
      } catch (error) {
        console.error('[DeviceOrientationManager] Permission request failed:', error);
        return false;
      }
    } else {
      // No permission needed for non-iOS or older iOS
      this.permissionGranted = true;
      return true;
    }
  }

  /**
   * Check if DeviceOrientation API is supported
   */
  isSupported(): boolean {
    return typeof DeviceOrientationEvent !== 'undefined';
  }

  /**
   * Start listening to device orientation changes
   */
  startListening(callback: (data: DeviceOrientationData) => void): boolean {
    if (!this.isSupported()) {
      console.warn('[DeviceOrientationManager] DeviceOrientation API not supported');
      return false;
    }

    if (!this.permissionGranted) {
      console.warn('[DeviceOrientationManager] Permission not granted. Call requestPermission() first.');
      return false;
    }

    this.listener = callback;

    this.handleOrientationChange = (event: DeviceOrientationEvent) => {
      const { alpha, beta, gamma } = event;

      const filmingAngle = getFilmingAngle(beta, this.thresholds);
      const isLandscape = isLandscapeOrientation(gamma);

      const data: DeviceOrientationData = {
        alpha,
        beta,
        gamma,
        filmingAngle,
        isLandscape
      };

      if (this.listener) {
        this.listener(data);
      }
    };

    window.addEventListener('deviceorientation', this.handleOrientationChange);
    debugLog('[DeviceOrientationManager] Started listening to device orientation');
    return true;
  }

  /**
   * Stop listening to device orientation changes
   */
  stopListening(): void {
    if (this.handleOrientationChange) {
      window.removeEventListener('deviceorientation', this.handleOrientationChange);
      this.handleOrientationChange = null;
      this.listener = null;
      debugLog('[DeviceOrientationManager] Stopped listening to device orientation');
    }
  }

  /**
   * Update detection thresholds
   */
  updateThresholds(thresholds: Partial<OrientationThresholds>): void {
    this.thresholds = { ...this.thresholds, ...thresholds };
  }
}

/**
 * Get recommended filming guidance based on angle and baby age
 */
export interface FilmingGuidance {
  angle: FilmingAngle;
  recommendation: string;
  isIdeal: boolean;
}

export function getFilmingGuidance(
  filmingAngle: FilmingAngle,
  infantAgeMonths: number
): FilmingGuidance {
  // Newborns (0-3 months) - typically in crib/bassinet
  if (infantAgeMonths < 3) {
    if (filmingAngle === 'overhead') {
      return {
        angle: filmingAngle,
        recommendation: 'Good overhead angle for crib recording',
        isIdeal: true
      };
    } else if (filmingAngle === 'floor') {
      return {
        angle: filmingAngle,
        recommendation: 'For best results, film from above when baby is in crib',
        isIdeal: false
      };
    }
  }

  // Older babies (6+ months) - typically on floor
  if (infantAgeMonths >= 6) {
    if (filmingAngle === 'floor') {
      return {
        angle: filmingAngle,
        recommendation: 'Good angle for floor play recording',
        isIdeal: true
      };
    } else if (filmingAngle === 'overhead') {
      return {
        angle: filmingAngle,
        recommendation: 'For floor play, hold phone at baby\'s level',
        isIdeal: false
      };
    }
  }

  // Mid-range (3-6 months) - either works
  if (filmingAngle === 'overhead' || filmingAngle === 'floor') {
    return {
      angle: filmingAngle,
      recommendation: 'Recording angle looks good',
      isIdeal: true
    };
  }

  // Transitional or unknown
  return {
    angle: filmingAngle,
    recommendation: 'Adjust phone angle for clearer view',
    isIdeal: false
  };
}
